import { LocalNotifications } from '@capacitor/local-notifications';
import { PUSH_CHANNELS } from '@/services/push/notificationPermission';
import { PUSH_TYPES, pickPushData } from '@/services/push/pushTarget';

// Push recebido com o app ABERTO vira notificação local do sistema, igual à que
// chegaria com o app fechado: o iOS não mostra o push em primeiro plano
// (presentationOptions []) e o Android nunca mostra. Mesmo canal (Android) e
// mesma categoria (iOS) do push, então o aluno que desligou um tipo de aviso
// também não vê este. O toque cai no mesmo roteador do push (handlePushTap).
//
// Sem permissão de notificação não mostra nada: o sino já é recarregado por
// quem chama.

// Marca no `extra` para o toque saber que a notificação local é um push.
const SOURCE = 'ead-push-fg';
// Faixa própria de ids (int de 32 bits no Android), longe do 9001 do download.
const ID_BASE = 1642000000;
const ID_SPAN = 100000000;
const ICON_COLOR = '#0564B1'; // @color/push_notification_color

let _seq = 0;

// Canal Android e categoria iOS de cada tipo (os mesmos que o servidor manda no
// payload). Lido na hora, não no carregamento do módulo.
function kindOf (type) {
  if (type === PUSH_TYPES.COMMENT_REPLY) return { channelId: PUSH_CHANNELS.replies.id, actionTypeId: 'COMMENT_REPLY' };
  if (type === PUSH_TYPES.CERTIFICATE_ISSUED) return { channelId: PUSH_CHANNELS.certificates.id, actionTypeId: 'CERTIFICATE_ISSUED' };
  if (type === PUSH_TYPES.SUBSCRIPTION_LATE) return { channelId: PUSH_CHANNELS.payments.id, actionTypeId: 'SUBSCRIPTION_LATE' };
  return { channelId: PUSH_CHANNELS.content.id, actionTypeId: 'CONTENT_NEW' };
}

function nextId () {
  _seq = (_seq + 1) % 1000;
  return ID_BASE + ((Date.now() + _seq) % ID_SPAN);
}

const text = value => (typeof value === 'string' ? value.trim() : '');

// `notification` é o `event.notification` do FirebaseMessaging; `data`, o payload
// do push. `null` = nada para mostrar.
export function buildForegroundNotification (notification, data, id) {
  const n = notification || {};
  const title = text(n.title);
  const body = text(n.body);
  if (!title && !body) return null;
  const picked = pickPushData(data);
  const kind = kindOf(picked.type);
  return {
    id,
    title,
    body,
    channelId: kind.channelId,
    // Categoria iOS (IOS_CATEGORIES): o texto da prévia oculta na tela bloqueada.
    actionTypeId: kind.actionTypeId,
    smallIcon: 'ic_notification',
    iconColor: ICON_COLOR,
    extra: { source: SOURCE, data: picked }
  };
}

// Dados do push de uma notificação local tocada; `null` se ela não veio de push.
export function pushDataFromLocalTap (event) {
  const extra = event && event.notification && event.notification.extra;
  if (!extra || extra.source !== SOURCE) return null;
  return extra.data && typeof extra.data === 'object' ? extra.data : {};
}

export async function showForegroundNotification (notification, data) {
  const local = buildForegroundNotification(notification, data, nextId());
  if (!local) return false;
  try {
    const { display } = await LocalNotifications.checkPermissions();
    if (display !== 'granted') return false;
    await LocalNotifications.schedule({ notifications: [local] });
    return true;
  } catch (e) {
    console.warn('[push] notificação em primeiro plano falhou:', e?.message || e);
    return false;
  }
}

// Toque numa notificação local. As de download não têm a marca e são ignoradas.
export function listenForegroundNotificationTaps (onPushTap) {
  return LocalNotifications.addListener('localNotificationActionPerformed', event => {
    const data = pushDataFromLocalTap(event);
    if (data) onPushTap(data);
  }).catch(() => {});
}
